import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TextField,
  Tooltip,
  Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import UndoIcon from '@material-ui/icons/Undo';
import styled from 'styled-components';
import {
  ProcedureCostDetailsInput,
  StaffCost,
  StaffPresence,
  durationMinutesFromSeconds,
  durationSecondsFromMinutes,
  formatCost,
  formatDuration,
} from './procedureCostMock';
import {
  EDIT_STAFF_ATTRIBUTION_SECONDS,
  EditMutationData,
  PROCEDURE_COST_QUERY,
  ProcedureCostPayload,
  ProcedureCostQueryData,
  ProcedureCostStaffPayload,
  REVERT_STAFF_ATTRIBUTION,
  RevertMutationData,
} from './procedureCostQueries';

interface Props extends ProcedureCostDetailsInput {
  open: boolean;
  onClose: () => void;
}

// Narrow the server's string `presence` to the local literal union.
// Anything outside the two Phase-1 values falls back to 'tracked-here' —
// the engine only emits these two, so the fallback is defensive.
function toPresence(raw: string): StaffPresence {
  return raw === 'untracked-assigned' ? 'untracked-assigned' : 'tracked-here';
}

function mapStaffRow(row: ProcedureCostStaffPayload): StaffCost {
  return {
    staffId: row.staffId,
    name: row.name,
    role: row.role,
    hourlyRate: row.hourlyRate ?? 0,
    presence: toPresence(row.presence),
    originalDurationSeconds: row.originalDurationSeconds,
    effectiveDurationSeconds: row.effectiveDurationSeconds,
    costCents: row.costCents,
    isManuallyEdited: row.isManuallyEdited,
  };
}

const trimTrailingPunctuation = (s: string): string => s.replace(/[\s,.]+$/, '');

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.65;
  margin-bottom: 6px;
  gap: 16px;
`;

const HeaderTopMeta = styled.span`
  font-weight: 500;
`;

// Two-row time block — scheduled (planned) over actual (as recorded). Sits
// in its own column to the right of the title block so the two stacked rows
// don't push the title down (the column is independent of the title's flow).
// Shares the small-caps "metadata" styling with HeaderTopMeta (uppercase,
// 0.75rem, low opacity) so it reads as a header-level label, not body text.
// tabular-nums keeps digits aligned across both rows.
const HeaderTimeBlock = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 12px;
  row-gap: 2px;
  text-align: right;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.65;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const HeaderTimeLabel = styled.span`
  font-weight: 500;
  opacity: 0.75;
  text-align: left;
`;

const HeaderTimeValue = styled.span`
  font-weight: 500;
`;

const ProcedureName = styled(Typography)`
  && {
    font-weight: 500;
    line-height: 1.2;
  }
`;

const ProcedureSubLine = styled(Typography)`
  && {
    margin-top: 2px;
    opacity: 0.85;
  }
`;

const PresenceBadge = styled(Chip)<{ $tone: 'warning' | 'info' | 'edit' }>`
  && {
    height: 20px;
    font-size: 0.7rem;
    background-color: ${p =>
      p.$tone === 'warning'
        ? 'rgba(245, 158, 11, 0.18)'
        : p.$tone === 'info'
          ? 'rgba(94, 179, 255, 0.18)'
          : 'rgba(167, 139, 250, 0.18)'};
    color: ${p => (p.$tone === 'warning' ? '#fbbf24' : p.$tone === 'info' ? '#5eb3ff' : '#a78bfa')};
    border: 1px solid
      ${p =>
        p.$tone === 'warning'
          ? 'rgba(245, 158, 11, 0.4)'
          : p.$tone === 'info'
            ? 'rgba(94, 179, 255, 0.4)'
            : 'rgba(167, 139, 250, 0.4)'};
    & .MuiChip-label {
      padding: 0 8px;
    }
  }
`;

const StaffRow = styled(TableRow)`
  && > .MuiTableCell-root {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const CellContent = styled.div<{ $align?: 'right' }>`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: ${p => (p.$align === 'right' ? 'flex-end' : 'flex-start')};
`;

// Actions column (edit + revert) — sits in its own headerless column to the
// right of Time in OR. Edit always renders adjacent to the column-start;
// revert is conditional and falls into the trailing slot. The HiddenIconButton
// placeholder keeps edit's position fixed across rows whether or not the row
// has an active override.
const ActionsCellContent = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
`;

// TableCell with no left padding — used for the actions column so the edit
// icon sits flush against the Time in OR cell's right edge instead of
// floating ~24px away (Material UI's default size="small" left padding is
// 16px, and the preceding cell adds 24px right padding, so the unstyled gap
// between time text and edit icon would be ~40px).
const ActionsCell = styled(TableCell)`
  && {
    padding-left: 0;
  }
`;

// Tightened right padding for the Time in OR cell. With the value right-
// aligned, the text's right edge sits at cell-right − padding-right; the
// Material UI default of 24px is the dominant contributor to the visible
// gap between the time value and the edit icon. 4px keeps a small breathing
// room without pushing the text into the next cell.
const TimeCell = styled(TableCell)`
  && {
    padding-right: 4px;
  }
`;

const HiddenIconButton = styled(IconButton)`
  && {
    visibility: hidden;
    pointer-events: none;
  }
`;

const NameCellContent = styled.div`
  min-height: 40px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 12px;
  row-gap: 4px;
  padding: 4px 0;
`;

// Tracking indicator — two visual states so the dot ALONE differentiates
// without relying on row dimness or tooltip:
//   Tracked    → solid green dot (electronically tracked in this OR)
//   Scheduled  → hollow yellow dot (no electronic tracking, schedule-only)
type TrackingTone = 'tracked' | 'scheduled';

const TrackingIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const TrackingDot = styled.span<{ $tone: TrackingTone }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${p => (p.$tone === 'tracked' ? '#22c55e' : 'transparent')};
  border: ${p => (p.$tone === 'scheduled' ? '2px solid #fbbf24' : 'none')};
  box-sizing: border-box;
`;

// Staff name renders as a link to the staff-log page (in a new tab) when the
// kiosk's organizationId is resolvable from the URL. Falls back to plain text
// otherwise so the dialog stays usable in any rendering context. Inherits the
// dialog's text color so it doesn't clash with the dark theme; underline on
// hover signals the affordance.
const StaffNameLink = styled.a`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid rgba(94, 179, 255, 0.6);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const TotalCell = styled(TableCell)`
  && {
    font-weight: 700;
    font-size: 1.15rem;
    color: ${p => p.theme.palette.text.primary};
    border-top: 2px solid rgba(255, 255, 255, 0.18);
    border-bottom: none;
    padding-top: 16px;
    padding-bottom: 12px;
  }
`;

interface TrackingMeta {
  label: 'Tracked' | 'Scheduled';
  tone: TrackingTone;
  tooltip: string;
}

function trackingMeta(staff: StaffCost): TrackingMeta {
  if (staff.presence === 'untracked-assigned') {
    return {
      label: 'Scheduled',
      tone: 'scheduled',
      tooltip: 'No electronic tracking data — staff was scheduled / assigned. Cost assumed for the full OR window.',
    };
  }
  return {
    label: 'Tracked',
    tone: 'tracked',
    tooltip: 'Electronically tracked in this OR.',
  };
}

interface EditMeta {
  badge: string;
  tooltip: string;
}

function editMeta(staff: StaffCost): EditMeta | null {
  if (!staff.isManuallyEdited) return null;
  if (staff.presence === 'untracked-assigned') {
    return { badge: 'manually entered', tooltip: 'No tracking data — duration entered manually.' };
  }
  return {
    badge: 'Tracking overridden',
    tooltip: 'Tracking data was recorded; duration manually adjusted. Use the revert button to restore it.',
  };
}

interface RowEditState {
  staffId: string;
  draftMinutes: string;
}

function mapPayloadToStaff(payload: ProcedureCostPayload | null | undefined): StaffCost[] {
  if (!payload) return [];
  return payload.staff.map(mapStaffRow);
}

const ProcedureCostDialog: React.FC<Props> = ({ open, onClose, procedureId }) => {
  // Kiosk routing carries procedureId as a string; the resolver's `Long!`
  // arg expects a number. A non-numeric value would be a router bug; passing
  // 0 surfaces it as a "not found" from the resolver rather than a crash.
  const procedureIdNum = useMemo(() => {
    const parsed = Number.parseInt(procedureId, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [procedureId]);

  const { data, loading, error, refetch } = useQuery<ProcedureCostQueryData>(PROCEDURE_COST_QUERY, {
    variables: { procedureId: procedureIdNum },
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });

  const [editMutation, { loading: editSaving }] = useMutation<EditMutationData>(EDIT_STAFF_ATTRIBUTION_SECONDS);
  const [revertMutation] = useMutation<RevertMutationData>(REVERT_STAFF_ATTRIBUTION);

  const payload: ProcedureCostPayload | null = data?.procedureCost ?? null;
  const staff = useMemo(() => mapPayloadToStaff(payload), [payload]);
  const [editing, setEditing] = useState<RowEditState | null>(null);

  // Resolve organizationId from the kiosk URL once. The pathname pattern is
  // `/kiosk/{orgId}/...` (set by the kiosk Route stack); when resolvable, we
  // can build a staff-log link.
  const organizationId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const m = window.location.pathname.match(/^\/kiosk\/([^/]+)/);
    return m ? m[1] : null;
  }, []);

  const staffLogHref = useCallback(
    (s: StaffCost): string | null => {
      if (!organizationId || !payload) return null;
      return (
        `/kiosk/${encodeURIComponent(organizationId)}` +
        `/staff-log/${encodeURIComponent(s.staffId)}` +
        `/staff/${encodeURIComponent(payload.date)}`
      );
    },
    [organizationId, payload]
  );

  // Close the inline-edit popup whenever the payload changes (e.g. after a
  // mutation refetch or open/close cycle). Prevents the edit modal from
  // operating on a stale row id.
  React.useEffect(() => {
    setEditing(null);
  }, [payload]);

  const totalCostCents = useMemo(() => {
    const visible = staff.filter(s => s.costCents != null);
    if (visible.length === 0) return null;
    return visible.reduce((acc, s) => acc + (s.costCents as number), 0);
  }, [staff]);

  const titleText = payload ? trimTrailingPunctuation(payload.surgeonName) || 'Surgeon' : '';
  const subtitleParts = payload
    ? [
        payload.procedureType || '',
        payload.patientName
          ? payload.patientName +
            (payload.patientAge != null ? ` (${payload.patientAge}` : '') +
            (payload.patientAge != null && payload.patientSex ? `, ${payload.patientSex})` : '') +
            (payload.patientAge != null && !payload.patientSex ? ')' : '')
          : '',
      ].filter(Boolean)
    : [];

  const startEdit = useCallback((s: StaffCost) => {
    setEditing({
      staffId: s.staffId,
      draftMinutes: String(durationMinutesFromSeconds(s.effectiveDurationSeconds)),
    });
  }, []);

  const cancelEdit = useCallback(() => setEditing(null), []);

  // Refetch the procedureCost query after each mutation. The mutations
  // return a partial ProcedureCost (procedureId + staff only — no header
  // fields), so `cache.writeQuery` would clobber the cached header;
  // refetching is the cleanest way to keep duration/cost/edit-state on
  // the displayed rows in sync with what the SP just persisted. No
  // typePolicy is configured for ProcedureCost in apolloClient.ts, so
  // Apollo's default cache key would not match the mutation result to
  // the active query anyway.
  const saveEdit = useCallback(async () => {
    if (!editing) return;
    const minutes = Math.max(0, Math.round(Number(editing.draftMinutes) || 0));
    const seconds = durationSecondsFromMinutes(minutes);
    const staffIdNum = Number.parseInt(editing.staffId, 10);
    if (!Number.isFinite(staffIdNum)) {
      setEditing(null);
      return;
    }
    try {
      await editMutation({
        variables: { procedureId: procedureIdNum, staffId: staffIdNum, newSeconds: seconds },
      });
      await refetch();
    } finally {
      setEditing(null);
    }
  }, [editing, editMutation, procedureIdNum, refetch]);

  const revertRow = useCallback(
    async (staffId: string) => {
      const staffIdNum = Number.parseInt(staffId, 10);
      if (!Number.isFinite(staffIdNum)) return;
      await revertMutation({
        variables: { procedureId: procedureIdNum, staffId: staffIdNum },
      });
      await refetch();
    },
    [procedureIdNum, refetch, revertMutation]
  );

  const initialLoading = loading && !payload;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth onClick={e => e.stopPropagation()}>
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 16 }}>
          <Box flex={1} minWidth={0}>
            <HeaderTopRow>
              <HeaderTopMeta>{payload?.orRoomName || 'OR'}</HeaderTopMeta>
            </HeaderTopRow>
            <ProcedureName variant="h5">{titleText}</ProcedureName>
            {subtitleParts.length > 0 && (
              <ProcedureSubLine variant="body2" color="textSecondary">
                {subtitleParts.join(' · ')}
              </ProcedureSubLine>
            )}
          </Box>
          {payload && (
            <HeaderTimeBlock>
              <HeaderTimeLabel>Scheduled</HeaderTimeLabel>
              <HeaderTimeValue>
                {payload.scheduledStartText} – {payload.scheduledEndText} ·{' '}
                {formatDuration(payload.scheduledDurationSeconds)}
              </HeaderTimeValue>
              <HeaderTimeLabel>Actual</HeaderTimeLabel>
              <HeaderTimeValue>
                {payload.orStartText} – {payload.orEndText} · {formatDuration(payload.orDurationSeconds)}
              </HeaderTimeValue>
            </HeaderTimeBlock>
          )}
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ overflowX: 'auto' }}>
        {initialLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && !payload && (
          <Box p={2}>
            <Typography color="error">Unable to load cost details: {error.message}</Typography>
          </Box>
        )}
        {payload && (
          <Table size="small" style={{ tableLayout: 'fixed', minWidth: 840 }}>
            <colgroup>
              <col />
              <col style={{ width: 170 }} />
              <col />
              <col style={{ width: 115 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 100 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tracking</TableCell>
                <TableCell>Role</TableCell>
                <TimeCell align="right">Time in OR</TimeCell>
                <ActionsCell />
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map(s => {
                const tracking = trackingMeta(s);
                const edit = editMeta(s);
                // canRevert collapses to a single check now that
                // originalDurationSeconds is always present on the row
                // (per ADR-040). isManuallyEdited already encodes the
                // divergence; the explicit equality check guards against
                // a stale flag (defensive — the v2 GENERATED column
                // makes them strictly equivalent server-side).
                const canRevert = s.isManuallyEdited && s.effectiveDurationSeconds !== s.originalDurationSeconds;

                return (
                  <StaffRow key={s.staffId}>
                    <TableCell>
                      <NameCellContent>
                        {(() => {
                          const href = staffLogHref(s);
                          return href ? (
                            <Tooltip title="Open staff tracking">
                              <StaffNameLink href={href} target="_blank" rel="noopener noreferrer">
                                {s.name}
                              </StaffNameLink>
                            </Tooltip>
                          ) : (
                            <span>{s.name}</span>
                          );
                        })()}
                        {edit && (
                          <Tooltip title={edit.tooltip}>
                            <PresenceBadge $tone="edit" label={edit.badge} size="small" />
                          </Tooltip>
                        )}
                      </NameCellContent>
                    </TableCell>
                    <TableCell>
                      <CellContent>
                        <Tooltip title={tracking.tooltip}>
                          <TrackingIndicator>
                            <TrackingDot $tone={tracking.tone} />
                            {tracking.label}
                          </TrackingIndicator>
                        </Tooltip>
                      </CellContent>
                    </TableCell>
                    <TableCell>
                      <CellContent>{s.role}</CellContent>
                    </TableCell>
                    <TimeCell align="right">
                      <CellContent $align="right">{formatDuration(s.effectiveDurationSeconds)}</CellContent>
                    </TimeCell>
                    <ActionsCell>
                      <ActionsCellContent>
                        <Tooltip title="Edit time">
                          <IconButton size="small" onClick={() => startEdit(s)} aria-label="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canRevert ? (
                          <Tooltip
                            title={
                              s.presence === 'tracked-here'
                                ? `Revert to measured (${formatDuration(s.originalDurationSeconds)})`
                                : `Revert to original (${formatDuration(s.originalDurationSeconds)})`
                            }
                          >
                            <IconButton size="small" onClick={() => revertRow(s.staffId)} aria-label="Revert">
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <HiddenIconButton size="small" disabled aria-hidden tabIndex={-1}>
                            <UndoIcon fontSize="small" />
                          </HiddenIconButton>
                        )}
                      </ActionsCellContent>
                    </ActionsCell>
                    <TableCell align="right">
                      <CellContent $align="right">{formatCost(s.costCents)}</CellContent>
                    </TableCell>
                  </StaffRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TotalCell>Total</TotalCell>
                <TotalCell />
                <TotalCell />
                <TotalCell />
                <TotalCell />
                <TotalCell align="right">{formatCost(totalCostCents)}</TotalCell>
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <EditTimeDialog
        editing={editing}
        staff={staff}
        saving={editSaving}
        onChange={setEditing}
        onCancel={cancelEdit}
        onSave={saveEdit}
      />
    </Dialog>
  );
};

interface EditTimeDialogProps {
  editing: RowEditState | null;
  staff: StaffCost[];
  saving: boolean;
  onChange: (next: RowEditState | null) => void;
  onCancel: () => void;
  onSave: () => void;
}

const EditTimeDialog: React.FC<EditTimeDialogProps> = ({ editing, staff, saving, onChange, onCancel, onSave }) => {
  const editingStaff = editing ? (staff.find(s => s.staffId === editing.staffId) ?? null) : null;
  return (
    <Dialog open={editing !== null} onClose={onCancel} maxWidth="xs" fullWidth onClick={e => e.stopPropagation()}>
      <DialogTitle>Edit time in OR</DialogTitle>
      <DialogContent>
        {editingStaff && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {editingStaff.name} · {editingStaff.role}
          </Typography>
        )}
        <TextField
          type="number"
          label="Minutes"
          autoFocus
          fullWidth
          margin="normal"
          value={editing?.draftMinutes ?? ''}
          onChange={e => editing && onChange({ ...editing, draftMinutes: e.target.value })}
          onFocus={e => e.target.select()}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSave();
            }
          }}
          inputProps={{ min: 0, step: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={onSave} color="primary" variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcedureCostDialog;
